import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import ReactECharts from "echarts-for-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Transfer.css";
import { useSimulationData } from "../../../../hooks/useSimulationData";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { LOOSE, readColumn } from "../../utils/columnLookup";
import { parseSimulationDate } from "../../utils/simulationDate";
import {
  addColumnValue,
  finishColumnSum,
  startColumnSum,
} from "../../utils/columnSums";
import {
  buildDataZoom,
  darkTooltip,
  formatDate,
  getXAxisTitle,
} from "../../utils/chartOptions";
import Papa from "papaparse";

export default function TransferChart({
  csvData,

  onLoadingStateChange,
}) {
  const { setExportedTransferData } = useExportedData();

  const {
    dailyData,
    allTimeData,
    isLoading,
    loadingMessage,
    error,
    refetchData,
  } = useSimulationData(csvData, onLoadingStateChange);


  const [view, setView] = useState("yearly");
  const [chartData, setChartData] = useState([]);
  const [hasTriggeredRefetch, setHasTriggeredRefetch] = useState(false);

  /* The Date column reaches here as whatever string the result file carried -
   * "01/01/2040 00:00" from the sim, ISO from other writers - so every period
   * key below comes from a parse rather than a fixed offset into the stamp.
   * "01/01/2040".slice(0, 4) is "01/0", which matches Jan 1-9 of any year: a
   * yearly view built that way both truncates and cross-contaminates years.
   * One parse per row, positionally aligned with dailyData. */
  const periodKeys = useMemo(
    () => dailyData.map((row) => parseSimulationDate(row?.Date)),
    [dailyData],
  );

  /* First and last stamp the result actually covers. The native pickers below
   * reject a min/max they cannot read as ISO, which these keys always are. */
  const [firstKey, lastKey] = useMemo(() => {
    const readable = periodKeys.filter(Boolean);
    return [readable[0] ?? null, readable[readable.length - 1] ?? null];
  }, [periodKeys]);

  const availableYears = useMemo(
    () => Array.from(new Set(periodKeys.filter(Boolean).map((key) => key.year))),
    [periodKeys],
  );

  // Date state variables
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedyearRange, setSelectedYearRange] = useState("");
  const [currentSimulationTimeKey, setCurrentSimulationTimeKey] =
    useState(null);
  const [maxKnownCreationDate, setMaxKnownCreationDate] = useState(null);
  const formatSelectedPeriod = () => {
    if (view === "daily") {
      const [year, month, day] = selectedDate.split("-");
      return `${month}/${day}/${year}`; // MM/DD/YYYY
    } else if (view === "monthly") {
      const [year, month] = selectedMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${year}`;
    } else if (view === "yearly") {
      return selectedYear;
    } else if (view === "all time view") {
      return "";
    }
    return "";
  };

  const formatSelectedPeriodType = () => {
    if (view === "daily") {
      return "Day";
    } else if (view === "monthly") {
      return "Month";
    } else if (view === "yearly") {
      return "Year";
    } else if (view === "all time view") {
      return "All Time View";
    }
    return "";
  };


  // Force context to refetch data if no data is available
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        dailyData.length === 0 &&
        !isLoading &&
        !hasTriggeredRefetch &&
        refetchData
      ) {

        setHasTriggeredRefetch(true);
        refetchData();
      }
    }, 1000); // Wait 1 second for context to load naturally

    return () => clearTimeout(timer);
  }, [dailyData.length, isLoading, hasTriggeredRefetch, refetchData]);

  useEffect(() => {
    const storedKey = sessionStorage.getItem("currentSimulationTimeKey");
    const storedMaxDate = sessionStorage.getItem("maxKnownCreationDate");

    if (storedKey) {
      setCurrentSimulationTimeKey(storedKey);
    }
    if (storedMaxDate) {
      setMaxKnownCreationDate(storedMaxDate);
    }
  }, []);

  /* Transfer's fallback chain carries bare legacy column names
   * ("Energy Buy/Sell (kWh)", "Cost or Return ($)") for result shapes this repo
   * has no example of, so it keeps the loose fallback its own lookup used to
   * apply unconditionally. It now runs only after every exact key has missed -
   * see utils/columnLookup.js for the policy and its residual risk. */
  const readTransferColumn = useCallback(
    (entry, keys) => readColumn(entry, keys, { fallback: LOOSE }),
    [],
  );

  useEffect(() => {
    if (!firstKey) return;
    setSelectedDate(firstKey.day);
    setSelectedMonth(firstKey.month);
    setSelectedYear(firstKey.year);
    const years = availableYears.map(Number);
    if (!years.length) return;
    const minYear = years.reduce((a, b) => Math.min(a, b));
    const maxYear = years.reduce((a, b) => Math.max(a, b));
    setSelectedYearRange(`${minYear} - ${maxYear}`);
  }, [firstKey, availableYears]);

  /* Falls back to the first period the result covers rather than slicing on an
   * empty selector. The selectors are filled by an effect, so for one render
   * after a view switch the new view's selector is still "". The offset-slicing
   * version this replaced papered over that case only for the daily view, and
   * only by accident: `Date.startsWith("")` is true of every row, so switching
   * to Daily briefly plotted the WHOLE dataset under a one-day label, while
   * switching to Monthly or Yearly plotted nothing. Slicing on the first period
   * instead shows the right rows immediately in all three, and keeps the chart
   * mounted - an empty chartData unmounts ReactECharts, which disposes the
   * instance and takes the user's zoom range and legend selection with it. */
  const activeDay = selectedDate || firstKey?.day || "";
  const activeMonth = selectedMonth || firstKey?.month || "";
  const activeYear = selectedYear || firstKey?.year || "";

  // This useEffect filters dailyData based on the view and the corresponding selected date/month/year.
  useEffect(() => {
    if (view === "daily") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.day === activeDay),
      );
    } else if (view === "monthly") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.month === activeMonth),
      );
    } else if (view === "yearly") {
      setChartData(
        dailyData.filter((_, i) => periodKeys[i]?.year === activeYear),
      );
    } else if (view === "all time view") {
      setChartData(dailyData);
    }
  }, [
    periodKeys,
    view,
    activeDay,
    activeMonth,
    activeYear,
    selectedyearRange,
    dailyData,
  ]);



  const calculateTransferMetrics = (data) => {
  if (!data || data.length === 0) {
    return {
      maxPower: "0.00",
      minPower: "0.00",
      totalImport: "0.00",
      totalExport: "0.00",
      value: "0.00",
    };
  }

  let totalImport = 0;
  let totalExport = 0;
  let exportMax = 0;
  let importMax = 0;
  let totalCost = 0;
  /* The metered cash, which is what this tab's card now shows: the utility
   * bill. Same columns, and the same absence rule, as OverlayChart's copy of
   * this function -- both write the context field the Summary's Transfer card
   * reads, so they must publish the same figures. */
  const importCost = startColumnSum();
  const exportRevenue = startColumnSum();

  for (const entry of data) {
    addColumnValue(importCost, entry, "Totals Transfer Import Cost ($)");
    addColumnValue(exportRevenue, entry, "Totals Transfer Export Revenue ($)");
    const transferValue = parseFloat(readTransferColumn(entry, ["Totals Total Transfer (kWh)", "Energy Buy/Sell (kWh)", "Utility Grid Import (kWh)-Utility Grid Export (kWh)"])) || 0;

    if (transferValue > 0) {
      totalExport += transferValue;
      if (transferValue > exportMax) exportMax = transferValue;
    } else if (transferValue < 0) {
      const absVal = -transferValue;
      totalImport += absVal;
      if (absVal > importMax) importMax = absVal;
    }

    const costVal = parseFloat(readTransferColumn(entry, ["Totals Transfer Cost ($)", "Cost or Return ($)", "Costs Transfer Cost ($)"])) || 0;
    totalCost += costVal;
  }

  return {
    maxPower: exportMax.toFixed(2),
    minPower: importMax.toFixed(2),
    totalImport: totalImport.toFixed(2),
    totalExport: (-totalExport).toFixed(2),
    /* The engine's sign, not its negation. This writes the same context field
     * as OverlayChart's calculateTransferMetrics, so the two must agree or the
     * Grid card's number changes depending on which tab last reported. See the
     * fuller note there. */
    value: totalCost.toFixed(2),
    importCost: finishColumnSum(importCost),
    exportRevenue: finishColumnSum(exportRevenue),
  };
};


  // Update exported metrics whenever chartData changes
  useEffect(() => {
    const {
      maxPower,
      minPower,
      totalImport,
      totalExport,
      value,
      importCost,
      exportRevenue,
    } = calculateTransferMetrics(chartData);
    setExportedTransferData({
      maxPower,
      minPower,
      totalImport,
      totalExport,
      value,
      importCost,
      exportRevenue,
      view: formatSelectedPeriodType(),
      selectedPeriod: formatSelectedPeriod(),
      /* The period as ISO keys, so the page can slice the daily summary for
       * the demand charge the same way the Consumption page does. From the
       * ACTIVE keys, which is what chartData was sliced by. */
      periodView: view,
      selDate: activeDay,
      selMonth: activeMonth,
      selYear: activeYear,
    });
  }, [
    chartData,
    view,
    selectedDate,
    selectedMonth,
    selectedYear,
    activeDay,
    activeMonth,
    activeYear,
  ]);

  // Handle changes in the date/month/year input
  const handleDateChange = (e) => {
    if (view === "daily") {
      setSelectedDate(e.target.value);
    } else if (view === "monthly") {
      setSelectedMonth(e.target.value);
    } else if (view === "yearly") {
      setSelectedYear(e.target.value);
    } else if (view === "all time view") {
      setSelectedYearRange(e.target.value);
    }
  };

  useEffect(() => {
    if (onLoadingStateChange) {
      const loadingState = isLoading || dailyData.length === 0;

      onLoadingStateChange(loadingState);
    }
  }, [isLoading, dailyData.length, onLoadingStateChange]);

  // Unified function to get transfer value from a row




  const ControlsSkeleton = () => (
    <div className="controls-transfer">
      <Skeleton width={120} height={32} style={{ marginRight: "10px" }} />
      <Skeleton width={150} height={32} />
    </div>
  );

  const ChartSkeleton = () => (
    <SkeletonTheme baseColor="#f3f3f3" highlightColor="#e0e0e0">
      <div style={{ padding: "20px" }}>
        {/* Chart title skeleton */}
        <Skeleton width={200} height={24} style={{ marginBottom: "20px" }} />

        {/* Chart area skeleton */}
        <div
          style={{
            position: "relative",
            height: "350px",
            background: "#f8f9fa",
            borderRadius: "8px",
            padding: "20px",
          }}
        >
          {/* Y-axis labels skeleton */}
          <div
            style={{
              position: "absolute",
              left: "10px",
              top: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} width={40} height={12} />
            ))}
          </div>

          {/* Chart lines skeleton */}
          <div
            style={{
              marginLeft: "60px",
              marginBottom: "40px",
              height: "280px",
              position: "relative",
            }}
          >
            {/* Simulated chart line */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "10%",
                right: "10%",
                height: "2px",
                background:
                  "linear-gradient(90deg, #e0e0e0 0%, #f3f3f3 50%, #e0e0e0 100%)",
                borderRadius: "1px",
              }}
            />

            {/* Data points skeleton */}
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${15 + i * 10}%`,
                  top: `${30 + Math.sin(i) * 20}%`,
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#e0e0e0",
                }}
              />
            ))}
          </div>

          {/* X-axis labels skeleton */}
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "60px",
              right: "20px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} width={50} height={12} />
            ))}
          </div>
        </div>

        {/* Chart controls skeleton */}
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <Skeleton width={80} height={24} />
            <Skeleton width={60} height={24} />
          </div>
          <Skeleton width={100} height={24} />
        </div>
      </div>
    </SkeletonTheme>
  );

  const LoadingStateContent = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div style={{ padding: "20px" }}>
        <ChartSkeleton />
      </div>
    </SkeletonTheme>
  );



  return (
    <div className="chart-container-production">
      <h3>Transfer History</h3>

      {dailyData.length > 0 && !isLoading && (
        <div className="controls-transfer">
          <select
            onChange={(e) => setView(e.target.value)}
            value={view}
            style={{ marginRight: "10px", cursor: "pointer" }}
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="all time view">All Time View</option>
          </select>

          {view === "daily" && (
            <input
              type="date"
              onChange={handleDateChange}
              value={selectedDate}
              min={firstKey?.day || ""}
              max={lastKey?.day || ""}
              style={{ marginRight: "10px" }}
            />
          )}

          {view === "monthly" && (
            <input
              type="month"
              onChange={handleDateChange}
              value={selectedMonth}
              min={firstKey?.month || ""}
              max={lastKey?.month || ""}
              style={{ marginRight: "10px" }}
            />
          )}

          {view === "yearly" && (
            <select
              onChange={handleDateChange}
              value={selectedYear}
              className="view-dropdown"
              style={{ cursor: "pointer" }}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {isLoading && <ControlsSkeleton />}

      <div className="chart-wrapper" style={{ height: 600 }}>
        {error && !isLoading ? (
          <div style={{ padding: 16 }}>
            <div
              style={{
                border: "1px solid #fca5a5",
                background: "#fff1f2",
                color: "#991b1b",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Failed to load transfer timeseries
              </div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{String(error)}</div>
            </div>
            <button
              type="button"
              className="add-device-save-button"
              onClick={() => refetchData && refetchData()}
            >
              Retry
            </button>
          </div>
        ) : isLoading || dailyData.length === 0 ? (
          <LoadingStateContent />
        ) : chartData.length > 0 &&
          chartData.some(
            (entry) =>
              entry["Totals Total Transfer (kWh)"] !== undefined ||
              entry["Utility Grid Import (kWh)"] !== undefined ||
              entry["Energy Buy/Sell (kWh)"] !== undefined,
          ) ? (
          <React.Profiler
            id="ECharts"
            onRender={(id, phase, actualDuration) => {

            }}
          >
            <ReactECharts
              option={{
                // Animation configuration
                animation: true,
                animationDuration: 1000,
                animationEasing: "cubicInOut",
                animationThreshold: 2000,

                // Progressive rendering
                progressive: 500,
                progressiveThreshold: 3000,

                // Loading state
                loading: {
                  text: "Loading...",
                  color: "#675AFF",
                  textColor: "#675AFF",
                  maskColor: "rgba(255, 255, 255, 0.8)",
                  zlevel: 0,
                },

                // Tooltip configuration (hover)
                tooltip: darkTooltip({
                  fontSize: 12,
                  borderColor: "white",
                }),

                //Zoom and pan configuration
                dataZoom: buildDataZoom({
                  accent: "#675AFF",
                  fillerColor: "rgba(245, 245, 0, 0.2)",
                }),

                // Grid configuration
                grid: {
                  left: "40px",
                  right: "10px",
                  top: "20px",
                  bottom: "80px",
                  containLabel: true,
                },

                // X-axis configuration
                xAxis: {
                  type: "category",

                  data:
                    view === "all time view"
                      ? allTimeData.map((entry) => formatDate(entry.Date))
                      : chartData.map((entry) => formatDate(entry.Date)),
                  name: getXAxisTitle(view),
                  nameLocation: "middle",
                  nameGap: 30,
                  axisLine: {
                    lineStyle: {
                      color: "#999",
                    },
                  },
                  axisLabel: {
                    color: "#666",
                    rotate: 0,
                  },
                },

                // Y-axis configuration
                yAxis: {
                  type: "value",
                  name: "Energy (kWh)",
                  nameLocation: "middle",
                  nameGap: 50,
                  axisLine: {
                    lineStyle: {
                      color: "#999",
                    },
                  },
                  axisLabel: {
                    color: "#666",
                  },
                  splitLine: {
                    lineStyle: {
                      color: "#eee",
                    },
                  },
                },

                // Series configuration
                series: [
                  {
                    data:
                      view == "all time view"
                        ? allTimeData.map((entry) =>
                            -parseFloat(
                              readTransferColumn(entry, [
                                "Totals Total Transfer (kWh)",
                                "Energy Buy/Sell (kWh)",
                                "Utility Grid Import (kWh)-Utility Grid Export (kWh)",
                              ]),
                            ),
                          )
                        : chartData.map((entry) =>
                            -parseFloat(
                              readTransferColumn(entry, [
                                "Totals Total Transfer (kWh)",
                                "Energy Buy/Sell (kWh)",
                                "Utility Grid Import (kWh)-Utility Grid Export (kWh)",
                              ]),
                            ),
                          ),
                    type: "line",
                    smooth: true,
                    symbol: "circle",
                    symbolSize: 4,
                    sampling: "average",
                    progressive: view === "all time view" ? 1000 : 0,
                    progressiveThreshold: 3000,
                    animation: view === "all time view" ? false : true,
                    lineStyle: {
                      width: 2,
                      color: "rgba(103, 90, 255, 1)",
                    },
                    itemStyle: {
                      color: "rgba(103, 90, 255, 1)",
                    },
                    areaStyle: {
                      color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                          {
                            offset: 0,
                            color: "rgba(103, 90, 255, 0.3)",
                          },
                          {
                            offset: 1,
                            color: "rgba(103, 90, 255, 0.1)",
                          },
                        ],
                      },
                    },
                  },
                ],
              }}
              style={{ width: "100%", height: "100%" }}
              opts={{
                renderer: "canvas",
                devicePixelRatio: window.devicePixelRatio,
              }}
              notMerge={true}
              lazyUpdate={true}
              onChartReady={(chart) => {
                // Force resize after chart is ready
                setTimeout(() => {
                  chart.resize();
                }, 0);
              }}


            />
          </React.Profiler>
        ) : (
          <p className="no-results-message" style={{ textAlign: "center" }}>
            No results are currently available for the selected period.
          </p>
        )}
      </div>
    </div>
  );
}
